/**
 * Chantier : -
 * Regle metier : USR-R11, USR-R16, USR-R17 (PRODUIT/domaine/15_autorisations.md)
 * Decision : D-AUTH-02 (MFA TOTP admin_serme), D-AUTH-03
 * Hook racine d'authentification : suit la session Supabase, derive role/appRole/
 * etablissementId du JWT, charge userData et deconnecte si statut desactive
 * (USR-R13), expose AAL + factors MFA pour USR-R17 (admin_serme uniquement,
 * 1 appel reseau evite pour les autres roles), refresh JWT au visibilitychange
 * pour propager un signOut autre-onglet (USR-R12 + USR-R16).
 *
 * Reserve a AuthProvider : les composants consomment useAuthContext() (point de
 * souscription unique). Appeler useAuth() directement duplique les listeners.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import type { User, Factor } from '@supabase/supabase-js';
import type { Enums } from '@/types/supabase';

// role simplifie pour le layout (sidebar, routing)
export type UserRole = 'admin' | 'user' | null;
// role applicatif reel (pour le controle d'acces fin). `null` = etat applicatif
// (role pas encore charge), pas une valeur de l'enum PG.
export type AppRole = Enums<'user_role'> | null;

export type Aal = { current: 'aal1' | 'aal2'; next: 'aal1' | 'aal2' };

interface UserData {
  id: string;
  role: string;
  etablissement_id: string | null;
  prenom: string;
  nom: string;
  statut: string;
}

// role simplifie : admin_serme -> 'admin', les autres -> 'user'
function roleFromUser(authUser: User): UserRole {
  const appRole = authUser.app_metadata?.app_role;
  return appRole === 'admin_serme' ? 'admin' : 'user';
}

// role applicatif reel depuis le JWT
function appRoleFromUser(authUser: User): AppRole {
  const appRole = authUser.app_metadata?.app_role;
  if (appRole === 'admin_serme' || appRole === 'admin_client' || appRole === 'operateur') {
    return appRole;
  }
  return null;
}

// Extrait l'etablissement_id depuis le JWT app_metadata
function etablissementIdFromUser(authUser: User): string | null {
  return authUser.app_metadata?.etablissement_id || null;
}

export function useAuth() {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [aal, setAal] = useState<Aal | null>(null);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);
  const lastRefreshRef = useRef(0);

  const fetchUserData = useCallback(async (authUser: User) => {
    const { data } = await supabase
      .from('users')
      .select('id, role, etablissement_id, prenom, nom, statut')
      .eq('id', authUser.id)
      .single();

    // Si l'utilisateur est desactive en BDD, deconnecter immediatement
    if (data?.statut === 'desactive') {
      await supabase.auth.signOut();
      setUser(null);
      setUserData(null);
      return;
    }

    setUserData(data || null);
  }, []);

  const refreshMfaState = useCallback(async () => {
    // Appels SEQUENTIELS (pas Promise.all) : les deux methodes mfa.* prennent toutes
    // deux le lock `sb-*-auth-token` via navigator.locks. En parallele, la seconde
    // attend 10s puis throw NavigatorLockAcquireTimeoutError (vol de lock).
    // Batching des setState preserve : les 2 await se terminent avant la mise a jour
    // d'etat groupee, evitant l'etat transitoire (aal=aal2 mais factors=[]) qui
    // declenche la redirect loop AuthLayout <-> MfaEnrollPage apres reset+re-enrolement.
    const aalRes = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    const factorsRes = await supabase.auth.mfa.listFactors();
    const current = (aalRes.data?.currentLevel as 'aal1' | 'aal2' | null) ?? 'aal1';
    const next = (aalRes.data?.nextLevel as 'aal1' | 'aal2' | null) ?? 'aal1';
    setAal({ current, next });
    setFactors(factorsRes.data?.totp ?? []);
  }, []);

  const handleSignIn = useCallback(async (authUser: User) => {
    setUser(authUser);
    // RGPD-R08 (minimisation, PRODUIT/domaine/19_donnees_personnelles.md) : on n'envoie
    // que l'id (pseudonyme) a Sentry, pas l'email.
    Sentry.setUser({ id: authUser.id });
    Sentry.setTag('app_role', appRoleFromUser(authUser) ?? 'unknown');
    Sentry.setTag('etablissement_id', etablissementIdFromUser(authUser) ?? 'none');
    // refreshMfaState n'est utile que pour admin_serme (seul role concerne par MFA).
    // On evite 2 appels reseau Supabase au login pour les autres roles.
    const role = appRoleFromUser(authUser);
    const tasks: Promise<unknown>[] = [fetchUserData(authUser)];
    if (role === 'admin_serme') tasks.push(refreshMfaState());
    await Promise.all(tasks);
  }, [fetchUserData, refreshMfaState]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        await handleSignIn(session.user);
      }
      initialized.current = true;
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!initialized.current) return;

        if (event === 'SIGNED_IN' && session?.user) {
          handleSignIn(session.user);
        } else if (event === 'MFA_CHALLENGE_VERIFIED') {
          // mfa.verify reussi : refresh AAL + factors (le user reste le meme)
          refreshMfaState();
        } else if (event === 'SIGNED_OUT') {
          // Deconnexion subie (compte desactive, session revoquee) : ne pas laisser
          // les donnees du compte precedent en cache, comme signOut() ci-dessous.
          queryClient.clear();
          Sentry.setUser(null);
          setUser(null);
          setUserData(null);
          setAal(null);
          setFactors([]);
        }
      }
    );

    // Force un refresh du JWT quand l'onglet redevient visible.
    // Sans cela, un signOut({scope:'others'}) declenche depuis un autre onglet
    // n'invalide la session courante qu'a la fin de vie du JWT (1h par defaut),
    // car Supabase ne revoque que les refresh tokens cote serveur.
    // Throttle 30s : un user qui alt-tab rapidement ne genere qu'un seul refresh.
    // Le compteur est en useRef pour survivre aux re-mounts du hook (StrictMode, HMR).
    const THROTTLE_MS = 30_000;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefreshRef.current < THROTTLE_MS) return;
      lastRefreshRef.current = now;
      supabase.auth.refreshSession();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [handleSignIn, refreshMfaState, queryClient]);

  const signOut = useCallback(async () => {
    Sentry.setUser(null);
    await supabase.auth.signOut({ scope: 'local' });
    queryClient.clear();
    setUser(null);
    setUserData(null);
    setAal(null);
    setFactors([]);
  }, [queryClient]);

  return {
    user,
    userData,
    role: user ? roleFromUser(user) : null,
    appRole: user ? appRoleFromUser(user) : null,
    etablissementId: user ? etablissementIdFromUser(user) : null,
    aal,
    factors,
    refreshMfaState,
    loading,
    signOut,
  };
}
